import React, { useState } from 'react';
import SeriesForm from './SeriesForm';
import SeasonFormList from './SeasonFormList';
import EpisodeFormList from './EpisodeFormList';
import { useDispatch } from 'react-redux';
import { addSeries } from '../redux/slices/seriesSlice';
import { addSeason } from '../redux/slices/seasonSlice';
import { addEpisode } from '../redux/slices/episodeSlice';

const SeriesWizard = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [seriesData, setSeriesData] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para controlar múltiples envíos
  const [error, setError] = useState(null); // Estado para mostrar errores
  const dispatch = useDispatch();

  const handleSeriesSubmit = (data) => {
    setSeriesData(data);
    setStep(2);
  };

  const handleSeasonSubmit = (seasonsData) => {
    setSeasons(seasonsData);
    setStep(3);
  };

  const handleEpisodeSubmit = async () => {
    // Evitar múltiples envíos
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError(null); // Limpiar errores anteriores
    
    // Verificar datos de la serie
    console.log('📝 [SeriesWizard] Datos de la serie a enviar:', seriesData);
    
    // Primero, enviamos los datos de la serie al backend
    try {
      console.log('🚀 [SeriesWizard] Iniciando creación de serie...');
      const seriesResult = await dispatch(addSeries(seriesData)).unwrap();
      console.log('✅ [SeriesWizard] Serie creada exitosamente:', seriesResult);
      
      // Si llegamos aquí, la serie se creó correctamente
      const seriesId = seriesResult.id;
      console.log('🔑 [SeriesWizard] ID de la nueva serie:', seriesId);
      
      // Luego, creamos cada temporada con el ID de la serie
      console.log(`📋 [SeriesWizard] Temporadas a crear: ${seasons.length}`);
      for (const seasonData of seasons) {
        try {
          const seasonPayload = {
            ...seasonData,
            contentId: seriesId,
            contentType: 'series'
          };
          
          console.log('📝 [SeriesWizard] Creando temporada con datos:', seasonPayload);
          
          const seasonResult = await dispatch(
            addSeason({ seriesId: seriesId, seasonData: seasonPayload })
          ).unwrap();
          
          console.log('✅ [SeriesWizard] Temporada creada exitosamente:', seasonResult);
          
          // Finalmente, creamos cada episodio con el ID de la temporada
          if (seasonData.episodes && seasonData.episodes.length > 0) {
            console.log(`📺 [SeriesWizard] Episodios a crear en temporada ${seasonResult.id}: ${seasonData.episodes.length}`);
            for (const episodeData of seasonData.episodes) {
              try {
                console.log('📝 [SeriesWizard] Creando episodio con datos:', { ...episodeData, seasonId: seasonResult.id });
                
                await dispatch(
                  addEpisode({ 
                    seasonId: seasonResult.id, 
                    episodeData: { ...episodeData, seasonId: seasonResult.id } 
                  })
                ).unwrap();
              } catch (episodeError) {
                console.error('🚫 [SeriesWizard] Error al crear episodio:', episodeError);
                // Continuar con los siguientes episodios aunque haya errores
              }
            }
          }
        } catch (seasonError) {
          console.error('🚫 [SeriesWizard] Error al crear temporada:', seasonError);
          // Continuar con las siguientes temporadas aunque haya errores
        }
      }
      
      // Todo se completó exitosamente
      console.log('🎉 [SeriesWizard] Serie creada exitosamente con todas sus temporadas y episodios');
      alert('Serie creada exitosamente con todas sus temporadas y episodios');
      onClose();
    } catch (seriesError) {
      console.error('🚫 [SeriesWizard] Error al crear la serie:', seriesError);
      setError('Ocurrió un error al crear la serie. Verifica que todos los campos obligatorios estén completos.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              {step === 1 && "Paso 1: Información de la Serie"}
              {step === 2 && "Paso 2: Temporadas"}
              {step === 3 && "Paso 3: Episodios"}
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-300 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="mb-8">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 1 ? 'bg-blue-500' : 'bg-gray-700'} text-white`}>
                1
              </div>
              <div className={`flex-1 h-1 mx-2 ${step > 1 ? 'bg-blue-500' : 'bg-gray-700'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 2 ? 'bg-blue-500' : 'bg-gray-700'} text-white`}>
                2
              </div>
              <div className={`flex-1 h-1 mx-2 ${step > 2 ? 'bg-blue-500' : 'bg-gray-700'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 3 ? 'bg-blue-500' : 'bg-gray-700'} text-white`}>
                3
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-900 text-white p-3 mb-4 rounded border border-red-700">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="wizard-content mb-6">
            {step === 1 && <SeriesForm onSubmit={handleSeriesSubmit} initialData={seriesData} />}
            {step === 2 && <SeasonFormList 
              seriesData={seriesData} 
              initialSeasons={seasons} 
              onSubmit={handleSeasonSubmit} 
              onSelectSeason={setCurrentSeasonId} 
              contentType="series"
            />}
            {step === 3 && <EpisodeFormList 
              seasons={seasons} 
              currentSeasonId={currentSeasonId} 
              onSubmit={handleEpisodeSubmit} 
              onSelectSeason={setCurrentSeasonId} 
              contentType="series"
            />}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={step === 1}
              className={`px-4 py-2 rounded ${
                step === 1
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              Anterior
            </button>
            
            {step === 3 ? (
              <button
                onClick={handleEpisodeSubmit}
                disabled={isSubmitting}
                className={`${
                  isSubmitting 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white px-4 py-2 rounded flex items-center`}
              >
                {isSubmitting ? 'Procesando...' : 'Finalizar'}
                {isSubmitting && (
                  <svg className="animate-spin ml-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesWizard;
